import { useState, useEffect, useRef } from 'react';

/**
 * DateTimePicker - Styled calendar date and time picker
 *
 * Matches Raflr dark theme with primary accent colors.
 */
export default function DateTimePicker({
  value,
  onChange,
  minDate,
  maxDate,
  label,
  error,
  helperText,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) return new Date(value);
    if (minDate) return new Date(minDate);
    return new Date();
  });

  const containerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get current selected date/time
  const selectedDate = value ? new Date(value) : null;
  const selectedHour = selectedDate ? selectedDate.getHours() : 20;
  const selectedMinute = selectedDate ? selectedDate.getMinutes() : 0;

  // Calendar helpers
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Monday = 0

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Navigation
  function previousMonth() {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  }

  function nextMonth() {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  }

  // Check if a date is selectable
  function isDateSelectable(day) {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    date.setHours(23, 59, 59, 999); // End of day for comparison

    if (minDate && date < new Date(minDate)) return false;
    if (maxDate && date > new Date(maxDate)) return false;
    return true;
  }

  // Check if date is selected
  function isSelected(day) {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewDate.getMonth() &&
      selectedDate.getFullYear() === viewDate.getFullYear()
    );
  }

  // Check if date is today
  function isToday(day) {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === viewDate.getMonth() &&
      today.getFullYear() === viewDate.getFullYear()
    );
  }

  // Select a date
  function selectDate(day) {
    if (!isDateSelectable(day)) return;

    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    newDate.setHours(selectedHour, selectedMinute, 0, 0);
    onChange(newDate);
  }

  // Update time
  function updateTime(hour, minute) {
    const date = selectedDate ? new Date(selectedDate) : new Date();
    if (!selectedDate) {
      // If no date selected yet, use minimum date or today
      const baseDate = minDate ? new Date(minDate) : new Date();
      date.setFullYear(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    }
    date.setHours(hour, minute, 0, 0);
    onChange(date);
  }

  // Format display value
  function formatDisplayValue() {
    if (!selectedDate) return '';
    return selectedDate.toLocaleDateString('en-IE', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Generate calendar days
  const calendarDays = [];
  // Empty cells for days before first of month
  for (let i = 0; i < adjustedFirstDay; i++) {
    calendarDays.push(null);
  }
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-xs font-medium text-cream/70 uppercase tracking-wider mb-2">
          {label}
        </label>
      )}

      {/* Input Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-midnight border rounded-lg px-4 py-4 text-left flex items-center justify-between transition-all ${
          error
            ? 'border-error focus:ring-error/20'
            : 'border-primary/30 focus:border-primary focus:ring-2 focus:ring-primary/20'
        }`}
      >
        <span className={`font-mono ${selectedDate ? 'text-cream font-semibold' : 'text-cream/40'}`}>
          {selectedDate ? formatDisplayValue() : 'Select date and time...'}
        </span>
        <svg
          className={`w-5 h-5 text-primary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {helperText && !error && (
        <p className="text-cream/40 text-xs mt-2">{helperText}</p>
      )}
      {error && (
        <p className="text-error text-sm mt-1">{error}</p>
      )}

      {/* Dropdown Calendar */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-midnight-light border border-primary/20 rounded-xl shadow-xl shadow-black/50 overflow-hidden">
          {/* Month Navigation */}
          <div className="flex items-center justify-between p-4 border-b border-midnight-lighter">
            <button
              type="button"
              onClick={previousMonth}
              className="p-2 rounded-lg text-cream/60 hover:text-cream hover:bg-midnight transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-cream font-display font-semibold">
              {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="p-2 rounded-lg text-cream/60 hover:text-cream hover:bg-midnight transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-xs font-medium text-cream/40 py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => (
                <div key={index} className="aspect-square">
                  {day && (
                    <button
                      type="button"
                      disabled={!isDateSelectable(day)}
                      onClick={() => selectDate(day)}
                      className={`w-full h-full rounded-lg font-mono text-sm transition-all flex items-center justify-center ${
                        isSelected(day)
                          ? 'bg-primary text-midnight font-bold'
                          : isToday(day)
                          ? 'bg-primary/20 text-primary font-semibold'
                          : isDateSelectable(day)
                          ? 'text-cream hover:bg-midnight hover:text-primary'
                          : 'text-cream/20 cursor-not-allowed'
                      }`}
                    >
                      {day}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Time Picker */}
          <div className="p-4 border-t border-midnight-lighter bg-midnight/50">
            <label className="block text-xs font-medium text-cream/60 uppercase tracking-wider mb-3">
              Time
            </label>
            <div className="flex items-center gap-3">
              {/* Hour */}
              <div className="flex-1">
                <select
                  value={selectedHour}
                  onChange={(e) => updateTime(parseInt(e.target.value), selectedMinute)}
                  className="w-full bg-midnight border border-primary/30 rounded-lg px-3 py-2.5 text-cream font-mono font-semibold text-center focus:border-primary focus:ring-1 focus:ring-primary outline-none appearance-none cursor-pointer"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {i.toString().padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-2xl text-primary font-bold">:</span>
              {/* Minute */}
              <div className="flex-1">
                <select
                  value={selectedMinute}
                  onChange={(e) => updateTime(selectedHour, parseInt(e.target.value))}
                  className="w-full bg-midnight border border-primary/30 rounded-lg px-3 py-2.5 text-cream font-mono font-semibold text-center focus:border-primary focus:ring-1 focus:ring-primary outline-none appearance-none cursor-pointer"
                >
                  {[0, 15, 30, 45].map((min) => (
                    <option key={min} value={min}>
                      {min.toString().padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Done Button */}
          <div className="p-4 border-t border-midnight-lighter">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full py-3 bg-primary text-midnight font-semibold rounded-lg hover:bg-primary/90 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
